import { getStorage, ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { doc, updateDoc, setDoc } from 'firebase/firestore';
import app, { db } from './config';
import { getChatFileMaxBytes } from '../constants/fileLimits';

// Storage 인스턴스 생성
export const storage = getStorage(app);
export const STORAGE_UPLOAD_SCHEMA_VERSION = 1;
export const PRIVATE_UPLOAD_MAX_BYTES = 50 * 1024 * 1024;

const createUploadId = () => (
  window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
);

const sanitizeUploadName = (name = 'upload.bin') => (
  name.replace(/[^\w.\-가-힣]/g, '_').slice(-120) || 'upload.bin'
);

/**
 * 인증 사용자 전용 비공개 staging 경로로 원본 파일을 직접 업로드한다.
 * 백엔드는 descriptor의 prefix/UID/실제 blob 크기를 다시 검증한다.
 */
export const uploadPrivateAttachment = async (
  file,
  userId,
  {
    prefix,
    feature,
    onProgress,
    maxSizeBytes = PRIVATE_UPLOAD_MAX_BYTES,
    flatPath = false,
  } = {}
) => {
  if (!file || !userId || !prefix || !feature) {
    throw new Error('파일 업로드에 필요한 정보가 부족해요.');
  }
  if (file.size <= 0) {
    throw new Error('빈 파일은 업로드할 수 없어요.');
  }
  if (file.size > maxSizeBytes) {
    throw new Error(`파일은 ${Math.floor(maxSizeBytes / 1024 / 1024)}MB 이하여야 해요.`);
  }

  const uploadId = createUploadId();
  const safeName = sanitizeUploadName(file.name);
  const storagePath = flatPath
    ? `${prefix}/${userId}/${uploadId}_${safeName}`
    : `${prefix}/${userId}/${uploadId}/${safeName}`;
  const storageRef = ref(storage, storagePath);

  return await new Promise((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: file.type || 'application/octet-stream',
      customMetadata: {
        feature,
        schema_version: String(STORAGE_UPLOAD_SCHEMA_VERSION),
        upload_id: uploadId,
      },
    });
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        if (typeof onProgress === 'function' && snapshot.totalBytes > 0) {
          onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
        }
      },
      reject,
      () => resolve({
        schema_version: STORAGE_UPLOAD_SCHEMA_VERSION,
        upload_id: uploadId,
        storage_path: storagePath,
        name: file.name,
        type: file.type || 'application/octet-stream',
        content_type: file.type || 'application/octet-stream',
        size: file.size,
        feature,
      })
    );
  });
};

/**
 * 파일 업로드 함수
 * @param {File} file - 업로드할 파일
 * @param {string} path - 저장 경로
 * @returns {Promise<Object>} 업로드 결과
 */
export const uploadFile = async (file, path) => {
  try {
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return { success: true, url: downloadURL };
  } catch (error) {
    console.error('파일 업로드 오류:', error);
    return { success: false, error };
  }
};

/**
 * 생기부 첨부 파일을 비공개 Storage 경로에 업로드한다.
 * 백엔드 Admin SDK가 storage_path로만 읽으므로 다운로드 URL은 만들지 않는다.
 * @param {File} file - 업로드할 파일
 * @param {string} userId - Firebase Auth UID
 * @param {(progress: number) => void} [onProgress] - 0~100 진행률 콜백
 * @returns {Promise<Object>} 업로드된 파일 메타
 */
export const uploadSaenggibuAttachment = async (file, userId, onProgress) => {
  return uploadPrivateAttachment(file, userId, {
    prefix: 'saenggibu_uploads',
    feature: 'saenggibu',
    onProgress,
    flatPath: true,
  });
};

export const uploadScoringAttachment = async (file, userId, feature, onProgress) => (
  uploadPrivateAttachment(file, userId, {
    prefix: 'scoring_uploads',
    feature,
    onProgress,
  })
);

export const uploadChatAttachment = async (file, userId, onProgress) => (
  uploadPrivateAttachment(file, userId, {
    prefix: 'chat_uploads',
    feature: 'chat_attachment',
    onProgress,
    maxSizeBytes: getChatFileMaxBytes(file),
  })
);

/**
 * 파일 삭제 함수
 * @param {string} path - 삭제할 파일 경로
 * @returns {Promise<Object>} 삭제 결과
 */
export const deleteFile = async (path) => {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
    return { success: true };
  } catch (error) {
    console.error('파일 삭제 오류:', error);
    return { success: false, error };
  }
};

/**
 * 디렉토리 내 모든 파일 삭제
 * @param {string} dirPath - 삭제할 디렉토리 경로
 * @returns {Promise<Object>} 삭제 결과
 */
export const deleteFilesInDirectory = async (dirPath) => {
  try {
    const dirRef = ref(storage, dirPath);
    const listResult = await listAll(dirRef);
    
    await Promise.all(
      listResult.items.map(itemRef => deleteObject(itemRef))
    );
    
    return { success: true };
  } catch (error) {
    console.error('디렉토리 파일 삭제 오류:', error);
    return { success: false, error };
  }
};

/**
 * 학습지 저장 함수 (Firestore)
 * @param {Object} worksheetData - 학습지 데이터
 * @param {string} userId - 사용자 ID
 * @param {string} [worksheetId] - 기존 학습지 ID (수정 시)
 * @returns {Promise<Object>} 저장 결과
 */
export const saveWorksheetToFirestore = async (worksheetData, userId, worksheetId = null) => {
  try {
    const timestamp = new Date();
    
    // 저장할 기본 데이터
    const firestoreData = {
      ...worksheetData,
      userId,
      updatedAt: timestamp
    };
    
    let docRef;
    
    if (worksheetId) {
      // 기존 학습지 업데이트
      docRef = doc(db, 'worksheets', worksheetId);
      await updateDoc(docRef, firestoreData);
    } else {
      // 새 학습지 생성
      firestoreData.createdAt = timestamp;
      docRef = doc(db, 'worksheets');
      await setDoc(docRef, firestoreData);
    }
    
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Firestore 학습지 저장 오류:', error);
    return { success: false, error };
  }
}; 